import type { DescService, MessageInitShape } from '@bufbuild/protobuf';
import type { Observable } from 'rxjs';
import { concatMap } from 'rxjs';

import { deferAsyncIterable } from './defer-async-iterable';
import { EncodedBodyEnvelope } from './encoded-body-envelope';
import { Envelope } from './envelope';
import { EnvelopeDecodeStream } from './envelope-decode-stream';
import { getMethod } from './get-method';
import { DEFAULT_HEADERS } from './grpc-default-headers';
import { detectGrpcError } from './grpc-error';
import { FETCH_ORIGIN } from './grpc-fetch-origin';
import { FETCH_HEADERS } from './grpc-headers';
import type { GrpcNext } from './grpc-next';
import { GrpcTransport } from './grpc-transport';
import { MessageDecodeStream } from './message-decode-stream';
import { StartWithStream } from './start-with-stream';
import { GrpcContext } from './grpc-transport-context';

export interface FetchGrpcTransportOptions {
  readonly headers?: Headers;
  readonly fetch?: typeof globalThis.fetch;
}

export function* flat<T>(iterable: Iterable<Iterable<T>>): Iterable<T> {
  for (const sub of iterable) {
    yield* sub;
  }
}

export class FetchGrpcTransport extends GrpcTransport {
  private readonly origin: string;
  private readonly headers: Headers;
  private readonly fetch: typeof globalThis.fetch;

  constructor(
    origin: string,
    {
      headers = new Headers(),
      fetch = globalThis.fetch,
    }: FetchGrpcTransportOptions = {},
  ) {
    super();
    this.origin = origin;
    this.headers = headers;
    this.fetch = fetch.bind(globalThis);
  }

  protected internalSummon<S extends DescService, M extends keyof S['method']>(
    service: S,
    methodName: M,
    input: NoInfer<MessageInitShape<S['method'][M]['input']>>,
    context: GrpcContext,
  ): Observable<GrpcNext<S['method'][M]['output']>> {
    return deferAsyncIterable<GrpcNext<S['method'][M]['output']>[]>(
      async (signal) => {
        const method = getMethod(service, methodName);

        const url = `${this.origin}/${service.typeName}/${method.name}`;

        const headers = new Headers([
          ...DEFAULT_HEADERS,
          ...flat<[string, string]>(context.getValues(FETCH_HEADERS)),
        ]);

        const body = Envelope.encode(
          EncodedBodyEnvelope.toBinary(0, method.input, input),
        );

        const response = await this.fetch(url, {
          method: 'POST',
          headers,
          body,
          signal,
        });

        const error = detectGrpcError(response);

        if (error) {
          throw error;
        }

        // The response body is existed after detectGrpcError. The eslint error is expected here
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return response
          .body!.pipeThrough(new EnvelopeDecodeStream())
          .pipeThrough(new MessageDecodeStream(method.output))
          .pipeThrough(
            new StartWithStream([FETCH_ORIGIN.provide(this.origin)]),
          );
      },
    ).pipe(concatMap((values) => values));
  }
}
